import React, { useEffect, useRef } from 'react';

// SearchField - the one list-page search box (V2-077; DESIGN.md SRCH-01 to SRCH-03).
//
// Always visible in the filter panel's header bar (FiltersCard `search`), never behind a header
// Search button. Magnifier inside on the left; a clear "×" inside on the right once there is
// text; Enter runs the search, and so does a short pause in typing when `debounceMs` is set. The
// box has a fixed footprint, so typing, clearing or running a search never moves the page.

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Runs the search (Enter, the clear button, or after `debounceMs` of no typing). */
  onSearch: (value: string) => void;
  /** Names what is searched: "Name, email, or employee number". */
  placeholder: string;
  /** Accessible name; defaults to "Search". */
  label?: string;
  /** Also search after this many ms without typing. Off by default. */
  debounceMs?: number;
  className?: string;
}

export const SearchField: React.FC<SearchFieldProps> = ({
  value,
  onChange,
  onSearch,
  placeholder,
  label = 'Search',
  debounceMs,
  className = '',
}) => {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const change = (next: string) => {
    onChange(next);
    if (!debounceMs) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearch(next), debounceMs);
  };

  return (
    <div
      role="search"
      className={`wrap-search-field tw-relative tw-w-full sm:tw-w-72 tw-shrink-0 ${className}`}
      // A click in the search box must not also collapse / expand the filter panel.
      onClick={(e) => e.stopPropagation()}
    >
      <i
        className="bi bi-search tw-pointer-events-none tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-sm tw-text-content-muted"
        aria-hidden="true"
      />
      <input
        ref={inputRef}
        type="search"
        aria-label={label}
        value={value}
        placeholder={placeholder}
        onChange={(e) => change(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (timer.current) clearTimeout(timer.current);
            onSearch(value);
          }
        }}
        className="wrap-search-input tw-h-8 tw-w-full tw-appearance-none [&::-webkit-search-cancel-button]:tw-hidden [&::-webkit-search-decoration]:tw-hidden tw-rounded tw-border tw-border-line-input tw-bg-surface tw-pl-8 tw-pr-8 tw-text-sm tw-text-content placeholder:tw-text-content-muted focus:tw-border-primary focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary/25"
      />
      {/* The slot is always reserved, so the text never jumps when the "×" appears. It is the only
          clear icon: the browser's own search-cancel button is hidden above (V3-079). */}
      <span className="tw-absolute tw-right-1 tw-top-1/2 tw-flex tw-h-6 tw-w-6 -tw-translate-y-1/2 tw-items-center tw-justify-center">
        {value && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              if (timer.current) clearTimeout(timer.current);
              onChange('');
              onSearch('');
              inputRef.current?.focus();
            }}
            className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded tw-text-content-muted hover:tw-bg-surface-hover hover:tw-text-content"
          >
            <i className="bi bi-x-lg tw-text-xs" aria-hidden="true" />
          </button>
        )}
      </span>
    </div>
  );
};

export default SearchField;
